import type { Sessao } from './auth';
import { escreverNaApi as escrever, lerDaApi as ler } from './giro';
import type { MetaDaEquipe } from './vendas';

/**
 * BLOCO 13 — o front da administração (telas 29, 30, 34, 35 e 36). Espelha
 * `@inovapay/database/administracao`. Escritas com os três desfechos (confirmada, recusada,
 * incerta). Nenhuma resposta traz credencial, token ou senha.
 */

/* ------------------------------ 29 · regras e campanhas ------------------------------ */

export interface VersaoDeParametro {
  readonly versao: number;
  readonly premioBase: string;
  readonly pctAumento: string;
  readonly teto: string;
  readonly origem: string;
  readonly motivo: string;
  readonly por: string | null;
  readonly desde: string;
}
export interface RegrasVigentes {
  readonly vitamina: {
    readonly produtos: number;
    readonly ultimaMudanca: { readonly em: string; readonly por: string | null } | null;
  };
  readonly preVencido: readonly VersaoDeParametro[];
  readonly paradao: readonly VersaoDeParametro[];
  readonly metaSemanal: {
    readonly itens: number;
    readonly versao: number;
    readonly desde: string | null;
    readonly agendada: { readonly itens: number; readonly valeDesde: string } | null;
  };
  readonly publicacao: {
    readonly permitida: boolean;
    readonly porque: string;
    readonly valeDesde: string;
  };
}
export type EstadoDaCampanha =
  'RASCUNHO' | 'PROGRAMADA' | 'ATIVA' | 'ENCERRADA' | 'PAUSADA' | 'CANCELADA';
export type PublicoDaCampanha = 'TODOS' | 'BALCONISTAS' | 'ADMINISTRADORES';
export interface Campanha {
  readonly id: string;
  readonly nome: string;
  readonly descricao: string | null;
  readonly inicio: string | null;
  readonly fim: string | null;
  readonly publico: PublicoDaCampanha;
  readonly estado: EstadoDaCampanha;
  readonly produtos: number;
  readonly responsavel: string | null;
  readonly versao: number;
  readonly atualizadoEm: string;
}
export interface DetalheDaCampanha extends Campanha {
  readonly itens: readonly {
    readonly productId: string;
    readonly nome: string;
    readonly codigo: string | null;
  }[];
  readonly eventos: readonly {
    readonly tipo: string;
    readonly por: string | null;
    readonly motivo: string | null;
    readonly em: string;
  }[];
}
export interface SimulacaoDaRegra {
  readonly precoUnitario: string;
  readonly premioUnitario: string;
  readonly premioTotal: string;
  readonly explicacao: string;
  readonly bloqueio: unknown;
  readonly simulacao: true;
}

export const lerRegras = () => ler<RegrasVigentes>('/api/regras');
export const simularRegra = (
  s: Sessao,
  d: {
    productId: string;
    programa: 'vitamina' | 'pre_vencido' | 'paradao';
    faixa: 'a' | 'b' | 'c' | 'pv';
    preco?: string | null;
    quantidade: number;
  },
) => escrever<SimulacaoDaRegra>(s, 'POST', '/api/regras/simular', d);
export const lerCampanhas = () =>
  ler<{ campanhas: readonly Campanha[]; contagem: Readonly<Record<EstadoDaCampanha, number>> }>(
    '/api/campanhas',
  );
export const lerCampanha = (id: string) => ler<DetalheDaCampanha>(`/api/campanhas/${id}`);
export const salvarCampanha = (
  s: Sessao,
  d: {
    id?: string | null;
    versao?: number | null;
    nome: string;
    descricao?: string | null;
    inicio?: string | null;
    fim?: string | null;
    publico: PublicoDaCampanha;
    productIds: readonly string[];
  },
) => escrever<{ id: string; versao: number }>(s, 'POST', '/api/campanhas', d);
export const mudarCampanha = (
  s: Sessao,
  id: string,
  acao: 'publicar' | 'pausar' | 'retomar' | 'cancelar',
  versao: number,
  motivo?: string | null,
) =>
  escrever<{ estado: string; versao: number }>(s, 'POST', `/api/campanhas/${id}/${acao}`, {
    versao,
    motivo: motivo ?? null,
  });

/* ------------------------------ 30 · mural ------------------------------ */

export type TipoDeAviso = 'INFORMACAO' | 'ATENCAO' | 'CAMPANHA' | 'OPERACIONAL' | 'URGENTE';
export type PublicoDoAviso = 'TODOS' | 'BALCONISTAS' | 'ADMINISTRADORES' | 'AUDITORES';
export type EstadoDoAviso = 'RASCUNHO' | 'PROGRAMADO' | 'PUBLICADO' | 'EXPIRADO' | 'ARQUIVADO';
export interface Aviso {
  readonly id: string;
  readonly titulo: string;
  readonly corpo: string;
  readonly tipo: TipoDeAviso;
  readonly publico: PublicoDoAviso;
  readonly inicio: string | null;
  readonly fim: string | null;
  readonly fixado: boolean;
  readonly exigeConfirmacao: boolean;
  readonly estado: EstadoDoAviso;
  readonly publicadoPor: string | null;
  readonly publicadoEm: string | null;
  readonly criadoPor: string | null;
  readonly campanhaId: string | null;
  readonly leituras: {
    readonly publico: number;
    readonly vistos: number;
    readonly lidos: number;
    readonly confirmados: number;
  };
  readonly versao: number;
}
export type AvisoDoMural = Aviso & {
  readonly vistoEm: string | null;
  readonly lidoEm: string | null;
  readonly confirmadoEm: string | null;
};

export const lerAvisos = () =>
  ler<{ avisos: readonly Aviso[]; contagem: Readonly<Record<EstadoDoAviso, number>> }>(
    '/api/avisos',
  );
export const salvarAviso = (
  s: Sessao,
  d: {
    id?: string | null;
    versao?: number | null;
    titulo: string;
    corpo: string;
    tipo: TipoDeAviso;
    publico: PublicoDoAviso;
    inicio?: string | null;
    fim?: string | null;
    fixado: boolean;
    exigeConfirmacao: boolean;
  },
) => escrever<{ id: string; versao: number }>(s, 'POST', '/api/avisos', d);
export const mudarAviso = (
  s: Sessao,
  id: string,
  acao: 'publicar' | 'arquivar' | 'fixar' | 'desafixar',
  versao: number,
  motivo?: string | null,
) =>
  escrever<{ estado: string; versao: number }>(s, 'POST', `/api/avisos/${id}/${acao}`, {
    versao,
    motivo: motivo ?? null,
  });
export const lerMeuMural = () => ler<{ avisos: readonly AvisoDoMural[] }>('/api/mural');
export const registrarLeitura = (s: Sessao, id: string, nivel: 'visto' | 'lido' | 'confirmado') =>
  escrever<null>(s, 'POST', `/api/mural/${id}/leitura`, { nivel });

/* ------------------------------ 34 · configurações ------------------------------ */

export interface AreaDeConfiguracao {
  readonly area:
    | 'geral'
    | 'giro'
    | 'comercial'
    | 'metas'
    | 'qualidade'
    | 'alertas'
    | 'acesso'
    | 'integracoes'
    | 'dados';
  readonly titulo: string;
  readonly resumo: string;
  readonly acao: string;
  readonly link: string;
  readonly alerta: boolean;
}
export interface VisaoDasConfiguracoes {
  readonly geral: {
    readonly nomeDaOperacao: string;
    readonly versao: number;
    readonly fuso: string;
    readonly moeda: string;
    readonly formatoDeData: string;
  };
  readonly areas: readonly AreaDeConfiguracao[];
  readonly mudancas: readonly {
    readonly chave: string;
    readonly rotulo: string;
    readonly versao: number;
    readonly por: string | null;
    readonly em: string;
    readonly motivo: string;
  }[];
}
export const lerConfiguracoes = () => ler<VisaoDasConfiguracoes>('/api/configuracoes');
export const lerHistoricoDaConfiguracao = (chave: string) =>
  ler<{
    versoes: readonly {
      versao: number;
      valor: unknown;
      por: string | null;
      em: string;
      motivo: string;
      origem: string;
    }[];
  }>(`/api/configuracoes/${chave}/historico`);
export const salvarConfiguracaoGeral = (
  s: Sessao,
  d: {
    valor?: string | null;
    restaurarVersao?: number | null;
    motivo: string;
    versaoAtual: number;
  },
) =>
  escrever<{ versao: number; valor: string }>(s, 'PUT', '/api/configuracoes/geral', {
    chave: 'operacao_nome',
    ...d,
  });

/** O aplicativo do balconista: programas visíveis e modo manutenção (as chaves do 1.0). */
export interface AppDoBalconista {
  readonly programas: { vitamina: boolean; pre_vencido: boolean; paradao: boolean };
  readonly manutencao: { ativa: boolean; mensagem: string };
  /** SÓ_NUMEROS: o balconista não vê o que ganha nem o que vendeu em R$ (08/10). */
  readonly valores: 'MOSTRAR' | 'SO_NUMEROS';
  readonly versao: number;
}
export const lerAppDoBalconista = () => ler<AppDoBalconista>('/api/configuracoes/app-balconista');
export const salvarAppDoBalconista = (
  s: Sessao,
  d: {
    programas: AppDoBalconista['programas'];
    manutencao: AppDoBalconista['manutencao'];
    valores: AppDoBalconista['valores'];
    motivo: string;
    versaoAtual: number;
  },
) => escrever<AppDoBalconista>(s, 'PUT', '/api/configuracoes/app-balconista', d);

/** O sincronizador do HOS (tempo da busca, dias relidos, pausa) — SÓ os donos da loja. */
export interface ConfiguracaoDoHos {
  readonly vendasMin: number;
  readonly catalogoMin: number;
  readonly diasRelidos: number;
  readonly pausado: boolean;
  readonly versao: number;
}
export interface EstadoDoSincronizadorHos {
  readonly configuracao: ConfiguracaoDoHos;
  readonly limites: Record<
    'vendasMin' | 'catalogoMin' | 'diasRelidos',
    { readonly min: number; readonly max: number }
  >;
  readonly situacao: 'no_ar' | 'pausado' | 'parado' | 'nunca_rodou';
  readonly emExecucao: boolean;
  readonly ultimaBusca: {
    readonly inicio: string | null;
    readonly fim: string | null;
    readonly ok: boolean | null;
    readonly erro: string | null;
  };
  readonly ultimaBuscaDeProdutos: { readonly fim: string | null; readonly ok: boolean | null };
  readonly proximaBusca: string | null;
  readonly vistoEm: string | null;
  readonly buscaAgoraPendente: boolean;
  /** Todo dia até este já teve leitura completa de vendas no HOS. */
  readonly vendasCompletasAte: string | null;
  /** Devoluções do HOS que não entraram no cheque e esperam um dono revisar. */
  readonly devolucoesParaRevisar: number;
  readonly hoje: {
    readonly dia: string;
    readonly vendas: number;
    readonly devolucoes: number;
    readonly ultimaVenda: string | null;
  };
}
export const lerSincronizadorHos = () => ler<EstadoDoSincronizadorHos>('/api/configuracoes/hos');
export const salvarSincronizadorHos = (
  s: Sessao,
  d: Omit<ConfiguracaoDoHos, 'versao'> & { motivo: string; versaoAtual: number },
) => escrever<ConfiguracaoDoHos>(s, 'PUT', '/api/configuracoes/hos', d);
export const buscarAgoraNoHos = (s: Sessao) =>
  escrever<{ pedidoEm: string }>(s, 'POST', '/api/configuracoes/hos/buscar-agora', {});

export interface DevolucaoParaRevisar {
  readonly dia: string;
  readonly vendaId: string;
  readonly cupom: string;
  readonly cod: string;
  readonly nome: string | null;
  readonly qtd: number;
  readonly valor: string | null;
  readonly vendedor: string | null;
  readonly opDevol: string | null;
  readonly motivo: string;
  readonly vistaPrimeiroEm: string;
}
export const lerDevolucoesParaRevisar = () =>
  ler<{ total: number; itens: readonly DevolucaoParaRevisar[] }>(
    '/api/configuracoes/hos/devolucoes-para-revisar',
  );
export const revisarDevolucao = (
  s: Sessao,
  d: Pick<DevolucaoParaRevisar, 'dia' | 'vendaId' | 'cupom' | 'cod'> & { resolucao: string },
) =>
  escrever<{ jaEstava: boolean }>(
    s,
    'POST',
    '/api/configuracoes/hos/devolucoes-para-revisar/revisar',
    d,
  );

/* ------------------------------ 35 · integrações ------------------------------ */

export interface Integracoes {
  readonly atualizadoEm: string;
  readonly integracoes: readonly {
    readonly nome: string;
    readonly estado: 'saudavel' | 'degradado' | 'sem_dado' | 'nao_integrada';
    readonly detalhe: string;
  }[];
  readonly dominios: readonly {
    readonly dominio: string;
    readonly fonte: string;
    readonly ultimaAtualizacao: string | null;
    readonly frescor: string;
    readonly estado: 'saudavel' | 'atencao' | 'indisponivel' | 'nunca_integrado';
    readonly impacto: string;
    /** Explicação curta quando o número sozinho engana (ex.: "é a última venda lançada"). */
    readonly observacao?: string | null;
  }[];
  readonly metricas: readonly {
    readonly nome: string;
    readonly metodologia: string;
    readonly fonte: string;
  }[];
}
export const lerIntegracoes = () => ler<Integracoes>('/api/integracoes');

/* ------------------------------ 36 · saúde e auditoria ------------------------------ */

export type EstadoDoServico = 'saudavel' | 'degradado' | 'desconhecido' | 'indisponivel';
export interface SaudeDoSistema {
  readonly verificadoEm: string;
  readonly servicos: readonly {
    readonly nome: string;
    readonly estado: EstadoDoServico;
    readonly detalhe: string;
  }[];
  readonly seguranca: {
    readonly segundoFator: string;
    readonly sessoesAtivas: number;
    readonly bloqueiosOperacionais: number;
    readonly contasBloqueadas: number;
    readonly negadas24h: number;
  };
  readonly dados: {
    readonly backup: string;
    readonly retencao: string;
    readonly banco: { readonly tamanho: string | null; readonly migracoes: number };
  };
}
export interface EventoDaTrilha {
  readonly id: string;
  readonly em: string;
  readonly ator: string;
  readonly atorTipo: 'usuario' | 'sistema' | 'importacao' | 'integracao';
  readonly area: string;
  readonly acao: string;
  readonly tipo: string;
  readonly resultado: string;
  readonly entidade: string;
  readonly entidadeId: string;
}
export interface Trilha {
  readonly eventos: readonly EventoDaTrilha[];
  readonly total: number;
  readonly pagina: number;
  readonly pessoas: readonly { readonly id: string; readonly nome: string }[];
  readonly areas: readonly string[];
}
export const lerSaude = () => ler<SaudeDoSistema>('/api/sistema/saude');
export const lerTrilha = (f: {
  pessoa?: string | null;
  area?: string | null;
  dia?: string | null;
  pagina?: number;
}) => {
  const p = new URLSearchParams();
  if (f.pessoa) p.set('pessoa', f.pessoa);
  if (f.area) p.set('area', f.area);
  if (f.dia) p.set('dia', f.dia);
  if (f.pagina) p.set('pagina', String(f.pagina));
  return ler<Trilha>(`/api/sistema/trilha?${p.toString()}`);
};
export const lerEventoDaTrilha = (id: string) =>
  ler<EventoDaTrilha & { metadados: Record<string, unknown>; requestId: string | null }>(
    `/api/sistema/trilha/${id}`,
  );
export const lerVersao = () => ler<Record<string, unknown>>('/api/version');

/** Publicar nova versão dos parâmetros do pré-vencido/paradão (vale da próxima semana). */
export const publicarParametros = (
  s: Sessao,
  d: {
    programa: 'pre_vencido' | 'paradao';
    premioBase: string;
    pctAumento: string;
    teto: string;
    motivo: string;
    versaoAtual: number;
  },
) => escrever<{ versao: number; valeDesde: string }>(s, 'POST', '/api/regras/parametros', d);

/** Meta semanal (uma para todos): só os donos; vale da próxima semana. */
export const salvarMetaSemanal = (
  s: Sessao,
  d: { itens: number; motivo: string; versaoAtual: number },
) => escrever<{ versao: number; valeDesde: string }>(s, 'POST', '/api/regras/meta', d);

/* ------------------------------ meta da equipe com brinde ------------------------------ */

export interface VersaoDaMetaDaEquipe {
  readonly versao: number;
  readonly periodo: 'mes' | 'semana';
  readonly ref: string;
  readonly itens: number;
  readonly brinde: string | null;
  readonly por: string | null;
  readonly em: string;
  readonly motivo: string;
}
export interface MetaDaEquipeDoAdministrador {
  readonly meta: MetaDaEquipe | null;
  /** Quem fez quanto (só administrador — o balconista nunca recebe isto). */
  readonly porPessoa: readonly {
    readonly accountId: string;
    readonly nome: string;
    readonly itens: number;
  }[];
  readonly versao: number;
  readonly historico: readonly VersaoDaMetaDaEquipe[];
}
export const lerMetaDaEquipeDoAdministrador = () =>
  ler<MetaDaEquipeDoAdministrador>('/api/regras/meta-equipe');

/** Definir (ou desligar, com 0) a meta da equipe do período corrente. Vale na hora. */
export const salvarMetaDaEquipe = (
  s: Sessao,
  d: {
    periodo: 'mes' | 'semana';
    itens: number;
    brinde: string | null;
    motivo: string;
    versaoAtual: number;
  },
) => escrever<{ versao: number }>(s, 'POST', '/api/regras/meta-equipe', d);
